/**
 * Herbereken: reads Odoo again and builds a new snapshot, as a job on the
 * server that no request waits for. The host keeps one `RecomputeController`
 * for its page; the renderer's button and the empty state's button share it.
 * It serves Bezettingsgraad by default, and another app through its own
 * start and job requests, its summary's key and what a new snapshot makes stale.
 *
 * Its state comes from the snapshot summary (a running job, or why a start is
 * not possible now) and from what this page just did:
 * - `start()` posts; a 202 makes it `running` and polls the job every second;
 * - 409 (one runs already) refetches the summary, which names that job, and
 *   polls it;
 * - 429 (at most one start every 10 minutes) waits until `Retry-After`;
 * - 503 (Odoo not linked) makes it `unavailable`.
 *
 * A job ends when a poll says so, or when the summary does first (it names
 * the job as failed, or its latest snapshot is the one the job built): the
 * same ending, run once whichever sees it first. Then the summary is refetched
 * (and on success the meldingen and the apps' badges too), and the host moves
 * the page onto the new snapshot; the state stays `running` until that
 * summary is in. A failed job is in that summary (`job`), which the host's
 * banner reads. A start refused for any other reason reaches the host through
 * `onFailure`.
 *
 * It also keeps what the host's progress dialog shows (`progressView()`): the
 * job it follows (the one running, else the one that ended last), the latest
 * progress seen of it, and whether the dialog is open. `start()` opens it, and
 * so does `showProgress()` while a recompute runs (the button's click). It
 * closes by itself once nothing runs and there is no outcome to tell, and
 * 2,5 s after a success, which it tells only once the summary holds the new
 * snapshot. A success whose snapshot could not be loaded, and a failure, stay
 * until it is closed; `retryLoad()` asks for the summary again.
 */

import { hashKey, type QueryClient, type QueryKey } from "@tanstack/react-query";
import { useCallback, useSyncExternalStore } from "react";

import {
  type ApiError,
  type AppJob,
  APPS_KEY,
  type AppSnapshotSummary,
  BEZETTING_MELDINGEN_KEY,
  BEZETTING_SNAPSHOT_KEY,
  fetchBezettingJob,
  type JobProgress,
  type RecomputeAccepted,
  startBezettingRecompute,
} from "@/lib/api";
import { apiErrorCode } from "@/lib/api-errors";
import { logger } from "@/lib/logger";

import type { RecomputeHook, RecomputeState } from "./contract";
import { progressFraction } from "./recompute-progress";

/** How often a running job is asked about. */
export const POLL_MS = 1_000;
/** The longest wait between two asks while the job's state cannot be read. */
const POLL_MAX_MS = 30_000;
/** How long the dialog says a recompute succeeded before it closes by itself. */
export const SUCCESS_CLOSE_MS = 2_500;

/** What the progress dialog shows. The same object until something in it changes. */
export interface RecomputeProgressView {
  readonly open: boolean;
  /** The job followed: the one running, else the one that ended last; null before the first. */
  readonly job: AppJob | null;
  /** The latest progress seen of that job, kept once it ends (a job that is over reports none). */
  readonly progress: JobProgress | null;
  /** The job is over and the summary after it is in (after a success, holding its snapshot): the dialog can say how it ended. */
  readonly settled: boolean;
  /** The job succeeded, but the summary with its snapshot could not be loaded: the page still shows the previous one. */
  readonly loadFailed: boolean;
}

const NO_PROGRESS: RecomputeProgressView = {
  open: false,
  job: null,
  progress: null,
  settled: false,
  loadFailed: false,
};

export interface RecomputeOptions {
  queryClient: QueryClient;
  /** A start that failed other than by the three refusals the state shows (409, 429, 503). */
  onFailure: (error: unknown) => void;
  /** For tests: the clock, in ms. */
  now?: () => number;
  /** The start request: Bezettingsgraad's by default, another app's for that app. */
  start?: () => Promise<RecomputeAccepted>;
  /** The job's state: Bezettingsgraad's by default. */
  fetchJob?: (jobId: string) => Promise<AppJob>;
  /** The app's snapshot summary, which names its running job: Bezettingsgraad's by default. */
  summaryKey?: QueryKey;
  /** What a completed job makes stale besides the summary: Bezettingsgraad's meldingen and the apps' badges by default. */
  staleOnComplete?: readonly QueryKey[];
  /** The tag of its log lines. */
  logTag?: string;
}

/** What this page did, on top of the summary. */
type Local =
  | { readonly kind: "starting"; readonly at: string }
  | { readonly kind: "running"; readonly jobId: string; readonly startedAt: string }
  | { readonly kind: "waiting"; readonly until: string }
  | { readonly kind: "unavailable" };

const IDLE: RecomputeState = { kind: "idle" };
const UNAVAILABLE: RecomputeState = { kind: "unavailable" };

function sameState(a: RecomputeState, b: RecomputeState): boolean {
  if (a.kind !== b.kind) {
    return false;
  }
  if (a.kind === "running" && b.kind === "running") {
    return a.jobId === b.jobId && a.startedAt === b.startedAt && a.fraction === b.fraction;
  }
  if (a.kind === "waiting" && b.kind === "waiting") {
    return a.until === b.until;
  }
  return true;
}

function statusOf(error: unknown): number {
  return (error as Partial<ApiError> | null)?.status ?? 0;
}

/**
 * Whether the summary holds the snapshot a completed job built, or a later one.
 * A job and the snapshot it builds are one record on the server, with one id.
 */
function holdsSnapshotOf(summary: AppSnapshotSummary | undefined, job: AppJob): boolean {
  const snapshot = summary?.snapshot;
  if (!snapshot) {
    return false;
  }
  return (
    snapshot.id === job.id ||
    (job.finished_at !== null && Date.parse(snapshot.computed_at) > Date.parse(job.finished_at))
  );
}

export class RecomputeController implements RecomputeHook {
  private readonly queryClient: QueryClient;
  private readonly onFailure: (error: unknown) => void;
  private readonly now: () => number;
  private readonly startJob: () => Promise<RecomputeAccepted>;
  private readonly fetchJob: (jobId: string) => Promise<AppJob>;
  private readonly summaryKey: QueryKey;
  private readonly summaryHash: string;
  private readonly staleOnComplete: readonly QueryKey[];
  private readonly tag: string;

  private local: Local | null = null;
  /** Jobs known to be over, whatever an older summary still says. */
  private readonly finished = new Set<string>();
  private readonly listeners = new Set<(state: RecomputeState) => void>();
  private current: RecomputeState = IDLE;
  private attached = false;
  private unsubscribe: (() => void) | null = null;
  /** The job being polled, and its timer. */
  private polling: string | null = null;
  private pollTimer: ReturnType<typeof setTimeout> | null = null;
  private pollFailures = 0;
  private waitTimer: ReturnType<typeof setTimeout> | null = null;
  /** The job the dialog follows, and whether that came from a poll (fresher than the summary). */
  private followed: AppJob | null = null;
  private followedByPoll = false;
  /** The followed job's latest progress seen. */
  private lastProgress: JobProgress | null = null;
  /** The followed job is over and the summary after it is in. */
  private settled = false;
  /** The followed job succeeded, but the summary after it does not hold its snapshot. */
  private loadFailed = false;
  private open = false;
  private closeTimer: ReturnType<typeof setTimeout> | null = null;
  private view: RecomputeProgressView = NO_PROGRESS;

  constructor({
    queryClient,
    onFailure,
    now = Date.now,
    start = startBezettingRecompute,
    fetchJob = fetchBezettingJob,
    summaryKey = BEZETTING_SNAPSHOT_KEY,
    staleOnComplete = [BEZETTING_MELDINGEN_KEY, APPS_KEY],
    logTag = "[bezetting]",
  }: RecomputeOptions) {
    this.queryClient = queryClient;
    this.onFailure = onFailure;
    this.now = now;
    this.startJob = start;
    this.fetchJob = fetchJob;
    this.summaryKey = summaryKey;
    this.summaryHash = hashKey(summaryKey);
    this.staleOnComplete = staleOnComplete;
    this.tag = logTag;
    this.adoptSummaryJob();
    this.current = this.derive();
    this.syncView();
  }

  /** The current state; the same object until it changes (for `useSyncExternalStore`). */
  state = (): RecomputeState => this.current;

  /** The job followed, with its progress while it runs: the one running, else the one that ended last. */
  job = (): AppJob | null => this.followed;

  /** What the progress dialog shows; the same object until it changes. Subscribers hear of every change. */
  progressView = (): RecomputeProgressView => this.view;

  /** Opens the progress dialog while a recompute runs, whoever started it; does nothing otherwise. */
  showProgress = (): void => {
    if (this.current.kind !== "running") {
      return;
    }
    this.open = true;
    this.clearClose();
    this.refresh();
  };

  /** Closes the progress dialog; a running recompute goes on. */
  hideProgress = (): void => {
    this.open = false;
    this.clearClose();
    this.refresh();
  };

  /** Asks for the summary again after a success whose snapshot could not be loaded; does nothing otherwise. */
  retryLoad = (): void => {
    const job = this.followed;
    if (!this.loadFailed || job?.status !== "complete") {
      return;
    }
    this.loadAfter(job, job.id);
  };

  subscribe = (listener: (state: RecomputeState) => void): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  /** Follows the summary and runs the timers, from the page's mount to its unmount. */
  attach(): void {
    if (this.attached) {
      return;
    }
    this.attached = true;
    this.unsubscribe = this.queryClient.getQueryCache().subscribe((event) => {
      if (event.query.queryHash === this.summaryHash) {
        this.refresh();
      }
    });
    this.refresh();
  }

  detach(): void {
    this.attached = false;
    this.unsubscribe?.();
    this.unsubscribe = null;
    this.stopPolling();
    this.clearWait();
    this.clearClose();
  }

  start = (): void => {
    if (this.current.kind !== "idle") {
      return;
    }
    this.local = { kind: "starting", at: new Date(this.now()).toISOString() };
    this.followed = null;
    this.followedByPoll = false;
    this.lastProgress = null;
    this.settled = false;
    this.loadFailed = false;
    this.open = true;
    this.clearClose();
    this.refresh();
    this.startJob().then(
      ({ job }) => {
        logger.debug(this.tag, "recompute started", job.status);
        this.follow(job, true);
        this.local =
          job.status === "running"
            ? { kind: "running", jobId: job.id, startedAt: job.started_at }
            : null;
        if (job.status !== "running") {
          this.ended(job);
        }
        this.refresh();
      },
      (error: unknown) => this.startFailed(error),
    );
  };

  private startFailed(error: unknown): void {
    const code = apiErrorCode(error);
    logger.debug(this.tag, "recompute refused", code ?? statusOf(error));
    if (code === "recompute_running") {
      // The summary names the running job; until it is in, the button stays busy.
      void this.refetchSummary().finally(() => {
        this.local = null;
        this.refresh();
      });
      return;
    }
    if (code === "recompute_too_soon") {
      const seconds = (error as Partial<ApiError>).retryAfter;
      this.local =
        seconds === undefined
          ? null
          : { kind: "waiting", until: new Date(this.now() + seconds * 1000).toISOString() };
      void this.refetchSummary();
      this.refresh();
      return;
    }
    if (code === "odoo_not_configured") {
      this.local = { kind: "unavailable" };
      this.refresh();
      return;
    }
    this.local = null;
    this.refresh();
    // A 403 means the access went: the summary's refetch shows the page that says so.
    // Unless a developer views the dashboard as someone else, when nothing can be
    // started: that refusal is said.
    void this.refetchSummary();
    const status = statusOf(error);
    if ((status !== 401 && status !== 403) || code === "view_as_read_only") {
      this.onFailure(error);
    }
  }

  private derive(): RecomputeState {
    const summary = this.queryClient.getQueryData<AppSnapshotSummary>(this.summaryKey);
    const local = this.local;
    if (local?.kind === "running") {
      return {
        kind: "running",
        jobId: local.jobId,
        startedAt: local.startedAt,
        fraction: this.fractionOf(local.jobId),
      };
    }
    if (local?.kind === "starting") {
      // The start is on its way: no job id yet.
      return { kind: "running", jobId: "", startedAt: local.at, fraction: null };
    }
    const job = summary?.job;
    if (job?.status === "running" && !this.finished.has(job.id)) {
      return {
        kind: "running",
        jobId: job.id,
        startedAt: job.started_at,
        fraction: this.fractionOf(job.id),
      };
    }
    if (local?.kind === "unavailable" || summary?.recompute.reason === "odoo_not_configured") {
      return UNAVAILABLE;
    }
    const now = this.now();
    const until = [
      local?.kind === "waiting" ? local.until : null,
      summary?.recompute.reason === "too_soon" ? summary.recompute.next_start_at : null,
    ]
      .filter((at): at is string => at !== null && Date.parse(at) > now)
      .sort((a, b) => Date.parse(b) - Date.parse(a))[0];
    return until ? { kind: "waiting", until } : IDLE;
  }

  /** How far a job is, from the freshest word on it: 1 once complete, null when unknown. */
  private fractionOf(jobId: string): number | null {
    const summaryJob = this.queryClient.getQueryData<AppSnapshotSummary>(this.summaryKey)?.job;
    const job =
      this.followed?.id === jobId ? this.followed : summaryJob?.id === jobId ? summaryJob : null;
    if (job?.status === "complete") {
      return 1;
    }
    return job?.status === "running" ? progressFraction(job.progress) : null;
  }

  /**
   * Takes a newer word on a job: a different job resets what the dialog knew,
   * and the same job never goes back to running once its end is known, nor
   * takes the summary's older progress over a poll's.
   */
  private follow(job: AppJob, fromPoll: boolean): void {
    const previous = this.followed;
    if (previous?.id === job.id) {
      if (previous.status !== "running") {
        return;
      }
      if (job.status === "running" && !fromPoll && this.followedByPoll) {
        return;
      }
    } else {
      this.lastProgress = null;
      this.settled = false;
      this.loadFailed = false;
      this.followedByPoll = false;
    }
    this.followed = job;
    this.followedByPoll ||= fromPoll;
    if (job.progress) {
      this.lastProgress = job.progress;
    }
  }

  /**
   * The followed job's end when the summary saw it before a poll did: the job
   * as the summary names it once failed, or as the snapshot it built (one
   * record with the job) once complete. Null while the summary says nothing
   * new of it.
   */
  private endSeenInSummary(): AppJob | null {
    const followed = this.followed;
    if (followed?.status !== "running" || this.finished.has(followed.id)) {
      return null;
    }
    const summary = this.queryClient.getQueryData<AppSnapshotSummary>(this.summaryKey);
    if (summary?.job?.id === followed.id && summary.job.status !== "running") {
      return summary.job;
    }
    const snapshot = summary?.snapshot;
    if (snapshot?.id === followed.id) {
      return {
        ...followed,
        status: "complete",
        finished_at: snapshot.computed_at,
        duration_ms: snapshot.duration_ms,
        error: null,
        progress: null,
      };
    }
    return null;
  }

  /** Follows the job the summary names while it runs (someone else's, the daily one), and its end. */
  private adoptSummaryJob(): void {
    const job = this.queryClient.getQueryData<AppSnapshotSummary>(this.summaryKey)?.job;
    if (!job) {
      return;
    }
    const running = job.status === "running" && !this.finished.has(job.id);
    if (running || (job.status !== "running" && this.followed?.id === job.id)) {
      this.follow(job, false);
    }
  }

  /** Builds the dialog's view again when something in it changed; says whether it did. */
  private syncView(): boolean {
    const view = this.view;
    if (
      view.open === this.open &&
      view.job === this.followed &&
      view.progress === this.lastProgress &&
      view.settled === this.settled &&
      view.loadFailed === this.loadFailed
    ) {
      return false;
    }
    this.view = {
      open: this.open,
      job: this.followed,
      progress: this.lastProgress,
      settled: this.settled,
      loadFailed: this.loadFailed,
    };
    return true;
  }

  /** Works out the state again, tells the listeners when it or the dialog's view changed, and runs its timers. */
  private refresh(): void {
    if (this.local?.kind === "waiting" && Date.parse(this.local.until) <= this.now()) {
      this.local = null;
    }
    const end = this.endSeenInSummary();
    if (end) {
      // The same ending as a poll's; it refreshes again itself.
      this.ended(end);
      return;
    }
    this.adoptSummaryJob();
    const followed = this.followed;
    if (
      followed?.status === "complete" &&
      this.finished.has(followed.id) &&
      !this.settled &&
      holdsSnapshotOf(this.queryClient.getQueryData<AppSnapshotSummary>(this.summaryKey), followed)
    ) {
      // A summary that came in otherwise (the page's own refetch) holds the new snapshot.
      this.releaseLocal(followed.id);
      this.settle();
    }
    const next = this.derive();
    if (this.open && next.kind !== "running" && !this.settled && !this.loadFailed) {
      // Nothing runs and there is no outcome to tell (a refused start, a job that could not be read).
      this.open = false;
      this.clearClose();
    }
    const changed = !sameState(next, this.current);
    if (changed) {
      this.current = next;
    }
    if (this.syncView() || changed) {
      for (const listener of [...this.listeners]) {
        listener(this.current);
      }
    }
    if (!this.attached) {
      return;
    }
    if (next.kind === "running" && next.jobId !== "" && !this.finished.has(next.jobId)) {
      this.poll(next.jobId);
    } else {
      this.stopPolling();
    }
    this.clearWait();
    if (next.kind === "waiting") {
      this.waitTimer = setTimeout(
        () => {
          this.waitTimer = null;
          void this.refetchSummary();
          this.refresh();
        },
        Math.max(0, Date.parse(next.until) - this.now()) + 50,
      );
    }
  }

  private poll(jobId: string): void {
    if (this.polling === jobId) {
      return;
    }
    this.stopPolling();
    this.polling = jobId;
    this.pollFailures = 0;
    this.schedulePoll(jobId, POLL_MS);
  }

  private schedulePoll(jobId: string, delay: number): void {
    this.pollTimer = setTimeout(() => {
      this.pollTimer = null;
      this.fetchJob(jobId).then(
        (job) => {
          if (this.polling !== jobId) {
            return;
          }
          this.pollFailures = 0;
          if (job.status === "running") {
            this.follow(job, true);
            this.schedulePoll(jobId, POLL_MS);
            this.refresh();
            return;
          }
          this.polling = null;
          this.ended(job);
        },
        (error: unknown) => {
          if (this.polling !== jobId) {
            return;
          }
          const status = statusOf(error);
          if (status >= 400 && status < 500) {
            // Gone, or no longer ours to see: the summary says what holds now.
            this.polling = null;
            this.ended(null, jobId);
            return;
          }
          this.pollFailures += 1;
          this.schedulePoll(jobId, Math.min(POLL_MS * 2 ** this.pollFailures, POLL_MAX_MS));
        },
      );
    }, delay);
  }

  /**
   * A job that is over (or could not be read), seen by a poll or by the
   * summary: refetch what it changed, then let go of it. Runs once per job,
   * whichever saw the end first.
   */
  private ended(job: AppJob | null, jobId = job?.id ?? ""): void {
    if (this.finished.has(jobId)) {
      return;
    }
    this.finished.add(jobId);
    logger.debug(this.tag, "recompute ended", job?.status ?? "unknown");
    if (job) {
      this.follow(job, true);
    }
    if (job?.status === "complete") {
      for (const queryKey of this.staleOnComplete) {
        void this.queryClient.invalidateQueries({ queryKey, exact: true });
      }
    }
    this.loadAfter(job, jobId);
  }

  /**
   * Refetches the summary after a job ended. The dialog then says how it
   * ended: a failure at once, a success only once the summary holds its
   * snapshot, else that the new figures could not be loaded yet.
   */
  private loadAfter(job: AppJob | null, jobId: string): void {
    // Busy until the new summary is in, so the button never flashes back on in between.
    this.local = {
      kind: "running",
      jobId,
      startedAt: job?.started_at ?? new Date(this.now()).toISOString(),
    };
    this.loadFailed = false;
    this.refresh();
    void this.refetchSummary().finally(() => {
      this.releaseLocal(jobId);
      if (job && this.followed?.id === jobId && !this.settled) {
        const summary = this.queryClient.getQueryData<AppSnapshotSummary>(this.summaryKey);
        if (job.status === "complete" && !holdsSnapshotOf(summary, job)) {
          // The refetch failed: the page still shows the previous snapshot.
          logger.debug(this.tag, "new snapshot not loaded", jobId);
          this.loadFailed = true;
        } else {
          this.settle();
        }
      }
      this.refresh();
    });
  }

  /** The dialog can say how the followed job ended; on a success it closes soon after. */
  private settle(): void {
    this.settled = true;
    this.loadFailed = false;
    if (this.followed?.status === "complete" && this.open && this.attached) {
      this.scheduleClose();
    }
  }

  /** Lets go of the busy state kept for a job that ended. */
  private releaseLocal(jobId: string): void {
    if (this.local?.kind === "running" && this.local.jobId === jobId) {
      this.local = null;
    }
  }

  private refetchSummary(): Promise<void> {
    return this.queryClient.invalidateQueries({ queryKey: this.summaryKey, exact: true });
  }

  private stopPolling(): void {
    if (this.pollTimer !== null) {
      clearTimeout(this.pollTimer);
      this.pollTimer = null;
    }
    this.polling = null;
  }

  private clearWait(): void {
    if (this.waitTimer !== null) {
      clearTimeout(this.waitTimer);
      this.waitTimer = null;
    }
  }

  private scheduleClose(): void {
    this.clearClose();
    this.closeTimer = setTimeout(() => {
      this.closeTimer = null;
      this.open = false;
      this.refresh();
    }, SUCCESS_CLOSE_MS);
  }

  private clearClose(): void {
    if (this.closeTimer !== null) {
      clearTimeout(this.closeTimer);
      this.closeTimer = null;
    }
  }
}

/** A recompute hook's state in a component, which re-renders when it changes. */
export function useRecomputeState(hook: RecomputeHook): RecomputeState {
  const subscribe = useCallback((onChange: () => void) => hook.subscribe(onChange), [hook]);
  const state = useCallback(() => hook.state(), [hook]);
  return useSyncExternalStore(subscribe, state, state);
}

/** The progress dialog's view in a component, which re-renders when it changes. */
export function useRecomputeProgress(controller: RecomputeController): RecomputeProgressView {
  const subscribe = useCallback(
    (onChange: () => void) => controller.subscribe(onChange),
    [controller],
  );
  const view = useCallback(() => controller.progressView(), [controller]);
  return useSyncExternalStore(subscribe, view, view);
}
